import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import LoginPage from './components/recruiterProfiler/login/loginPage';
import SignUpPage from './components/recruiterProfiler/signUp/signUpPage';
import AuthLayout from './components/recruiterProfiler/auth/AuthLayout';
import Dashboard from './components/recruiterProfiler/dashBoard/dashBoard';
import CandidatesPage from './components/recruiterProfiler/candidates/candidatesPage';
import MatchingCandidatesPage from './components/recruiterProfiler/matchingCandidates/matchingCandidatesPage';
import CreateJobPage from './components/recruiterProfiler/createJobs/createJobPage';
import './App.css';
import JobDetailPage from './components/recruiterProfiler/jobDetail/jobDetail';
import JobsPage from './components/recruiterProfiler/jobs/jobsPage';
import CandidateDetailPage from './components/recruiterProfiler/candidates/candidateDetailPage';
import ProfilerLandingPage from './components/candidateProfiler/profilerLandingPage/profilerPage';
import ProfilerPage from './components/candidateProfiler/profilerEditingPage/profilerEditingPage';
import JobRecommendationsPage from './components/candidateProfiler/jobsRecommendationPage/jobsRecommendation';
import ProfilerJobDetailPage from './components/candidateProfiler/profilerJobDetails/profilerJobDetailPage';
import OpportunitiesPage from './components/candidateProfiler/jobs/opportunitiesPage';
import ResumeUpload from './components/candidateProfiler/profilerLandingPage/resumeUpload';
import { ToastProvider } from './common/toastSystem';
import SignupStatusPage from './components/recruiterProfiler/signUp/authorization';
import LeaderBoard from './components/recruiterProfiler/leaderBoard/leaderBoard';
import EmailValidation from './components/candidateProfiler/profilerLandingPage/emaildValidation';
import ProtectedRoute from './routes/ProtectedRoute';
import SessionTimeoutModal from './common/sessionTimeoutModal';
import AdminDashboard from './components/adminProfiler/dashboard/adminDashboard'
import ForgotPassword from './components/recruiterProfiler/forgotpassword/reset';
import ResetPassword from './components/recruiterProfiler/forgotpassword/resetPassword';
import ConsentPage from './components/candidateProfiler/profilerLandingPage/consentPage';
import unifiedAuthManager from './services/unifiedAuthManager'; 
import SmartMatchPage from './components/recruiterProfiler/jobDetail/smartmatchpage';

// Component to handle login redirects for already authenticated users
const LoginRedirect = ({ isResumeLogin = false }) => {
  const isAuthed = unifiedAuthManager.isAuthenticated();
  const userRoles = unifiedAuthManager.getUserRoles();
  const currentUser = unifiedAuthManager.getUnifiedUser();
  
  if (isAuthed) {
    const isCandidate = userRoles.some(role => 
      role.toLowerCase().includes('role_candidateuser')
    );
    const isHrrole = userRoles.some(role => 
      role.toLowerCase().includes('role_hr')
    );  
    
    // Redirect authenticated users to their respective dashboards
    if (isCandidate) {
      // If candidate doesn't have resume_id, redirect to profiler to upload resume
      if (!currentUser?.resume_id) {
        return <Navigate replace to="/profiler" />;
      }
      return <Navigate replace to={`/job-recommendations?candidateId=${currentUser.candidate_id}`} />;
    }
    else if (isHrrole) {
      return <Navigate replace to="/recruiterDashboard" />;
    }
    return <Navigate replace to="/adminDashboard" />;
  }
  
  // Not authenticated, show the login page
  return <LoginPage hideSignUpLink={isResumeLogin} />;
};


const ProfilerRouteGuard = ({ children }) => {
  const isAuthed = unifiedAuthManager.isAuthenticated();
  const userRoles = unifiedAuthManager.getUserRoles();
  const currentUser = unifiedAuthManager.getUnifiedUser();
  
  if (isAuthed) {
    const isCandidate = userRoles.some(role => 
      role.toLowerCase().includes('role_candidateuser')
    );
   
    // If authenticated as admin/recruiter, redirect to admin dashboard
    if (!isCandidate) {
      return <Navigate replace to="/adminDashboard" />;
    }
    
    // If authenticated candidate already has resume_id, redirect to job recommendations
    if (isCandidate && currentUser?.resume_id) {
      return <Navigate replace to={`/job-recommendations?candidateId=${currentUser.candidate_id}`} />;
    }
  }
  
  // Allow access for non-authenticated users or candidates without resume
  return children;
};

function App() {
  return (
    <ToastProvider>
      <Router>
        <div className="App overflow-hidden">
          <SessionTimeoutModal />
          <Routes>
            {/* Root route - redirect based on auth status */}
            <Route path="/" element={
              (() => {
                const isAuthed = unifiedAuthManager.isAuthenticated();
                const userRoles = unifiedAuthManager.getUserRoles();
                const currentUser = unifiedAuthManager.getUnifiedUser();
                console.log("Current User in App.js:", currentUser, isAuthed, userRoles);  
                
                if (isAuthed) {
                  const isCandidate = userRoles.some(role => 
                    role.toLowerCase().includes('role_candidateuser')
                  );
                  const isHrrole = userRoles.some(role => 
                    role.toLowerCase().includes('role_hr')
                  );
                  
                  if (isCandidate) {
                    console.log("Redirecting candidate from root to job recommendations",currentUser?.resume_id);  
                    // If candidate doesn't have resume_id, redirect to profiler to upload resume
                    if (!currentUser?.resume_id) {
                      return <Navigate replace to="/profiler" />;
                    }
                    return <Navigate replace to={`/job-recommendations?candidateId=${currentUser.candidate_id}`} />;
                  }
                  else if (isHrrole) {
                    return <Navigate replace to="/recruiterDashboard" />;
                    
                  }
                  return <Navigate replace to="/adminDashboard" />;
                }
                
                return <Navigate replace to="/resume-login" />;
              })()
            } />

            {/* Recruiter/Admin Login Routes */}
            <Route element={<AuthLayout leftPanel="login"/>}>
              <Route path="login" element={<LoginRedirect />} />
              <Route path="signup" element={<SignUpPage />} />
              <Route path="signup/:status" element={<SignupStatusPage />} />
            </Route>

            {/* Candidate Login Routes */}
            <Route element={<AuthLayout leftPanel="info" />}>
              <Route path="resume-login" element={<LoginRedirect isResumeLogin={true} />} />
              <Route path="candidate_signup" element={<SignUpPage />} />
              <Route path="candidate_signup/:status" element={<SignupStatusPage />} />
            </Route>

            {/* Protected Recruiter/Admin Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/candidates" element={<ProtectedRoute><CandidatesPage /></ProtectedRoute>} />
            {/* <Route path="dashboard/jobDetails/:jobId/matching" element={<MatchingCandidatesPage />} /> */}
            <Route path="/jobs/create" element={<ProtectedRoute><CreateJobPage /></ProtectedRoute>} />
            <Route path="jobs/jobDetails/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
            <Route path="dashboard/jobDetails/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
            <Route path="/candidates/:candidateId" element={<ProtectedRoute><CandidateDetailPage /></ProtectedRoute>} />
            <Route path='/adminDashboard' element={<ProtectedRoute><AdminDashboard/></ProtectedRoute>} />

            <Route path='/jobs/jobDetails/smartmatch/:mph_jobid' element={<ProtectedRoute><SmartMatchPage/></ProtectedRoute>} />

            {/* Candidate Routes */}
            <Route element={<ProfilerLandingPage />}>
              <Route path="profiler" element={<ProfilerRouteGuard><ResumeUpload /></ProfilerRouteGuard  >} />
              <Route path="candidate-login" element={<LoginPage hideSignUpLink={true} />} />
            </Route>
            {/* <Route path="/resume-preview" element={<ProfilerPage />} /> */}
             <Route path="/Resume-profile" element={<JobRecommendationsPage />} />  
            <Route path="/job-recommendations" element={<ProtectedRoute><JobRecommendationsPage /></ProtectedRoute>} />
            <Route path="/opportunities/:jobId" element={<ProtectedRoute><ProfilerJobDetailPage /></ProtectedRoute>} />
            <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesPage /></ProtectedRoute>} />
            <Route path="/consent" element={<ConsentPage />} />

            {/* Public Routes */}
             <Route path="/recruiterDashboard" element={<ProtectedRoute><LeaderBoard /></ProtectedRoute>} />
            <Route path="/activate_candidate" element={<EmailValidation />} />
            <Route path='/forgotPassword' element={<ForgotPassword/>} />
            <Route path='/resumeForgotPassword' element={<ForgotPassword/>} />
            <Route path='/resetPassword/' element={<ResetPassword/>} />
            <Route path='/resumeResetPassword/' element={<ResetPassword/>} />
          </Routes>
        </div>
      </Router>
    </ToastProvider>
  );
}

export default App;